import api from './api';
import { cleanParams } from './violations';
import { withMockFallback } from './mocks/withMockFallback';
import {
  approveChangeRequestMock,
  createChangeRequestMock,
  getAdminChangeRequestMock,
  getChangeRequestSummaryMock,
  listAdminChangeRequestsMock,
  listMyChangeRequestsMock,
  rejectChangeRequestMock,
} from './mocks/changeRequestsMock';

/**
 * Адаптер production-контракта Change Requests (C3).
 *
 * Роуты:
 *   POST /cameras/{camera_id}/change-requests   — подача клиентом;
 *   GET  /change-requests/{request_id}          — карточка (клиент и админ);
 *   GET  /admin/change-requests                 — разбор администратором;
 *   POST /admin/change-requests/{id}/approve    — тело { note };
 *   POST /admin/change-requests/{id}/reject     — тело { note }.
 *
 * Идентичность на бэкенде — `request_id`, тип — `request_type`. Алиасы
 * change_request_id/change_type живут только внутри UI и появляются здесь,
 * чтобы компоненты не знали, как поле называется в транспорте.
 */

const normalizeRequest = (item = {}) => ({
  ...item,
  change_request_id: item.change_request_id ?? item.request_id ?? '',
  change_type: item.change_type ?? item.request_type ?? '',
  proposed_by: item.proposed_by ?? (
    item.requested_by_user_id
      ? { user_id: item.requested_by_user_id, display_name: 'Клиент' }
      : null
  ),
  impact: item.impact ?? { features_to_enable: [], features_to_disable: [], affected_rules: [] },
});

/** Список приходит как `{ requests }` либо `{ items }` — форма UI одна. */
export const normalizeRequestList = (data, fallbackLimit = 50) => {
  const raw = Array.isArray(data?.requests)
    ? data.requests
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
  const items = raw.map(normalizeRequest);

  return {
    items,
    total: Number.isFinite(data?.total) ? data.total : items.length,
    limit: Number.isFinite(data?.limit) ? data.limit : fallbackLimit,
    offset: Number.isFinite(data?.offset) ? data.offset : 0,
  };
};

export const createChangeRequest = (cameraId, payload) =>
  withMockFallback(
    () => api
      .post(`/cameras/${cameraId}/change-requests`, payload)
      .then((response) => normalizeRequest(response.data)),
    () => createChangeRequestMock({ camera_id: Number(cameraId), ...payload }).then(normalizeRequest)
  );

export const listAdminChangeRequests = (params = {}) =>
  withMockFallback(
    () => api.get('/admin/change-requests', { params: cleanParams(params) }).then((response) => response.data),
    () => listAdminChangeRequestsMock(params)
  ).then((data) => normalizeRequestList(data, params.limit));

/** Карточка живёт на общем роуте: администратору он тоже доступен. */
export const getChangeRequest = (changeRequestId) =>
  withMockFallback(
    () => api.get(`/change-requests/${changeRequestId}`).then((response) => normalizeRequest(response.data)),
    () => getAdminChangeRequestMock(changeRequestId).then(normalizeRequest)
  );

/** Тело ReviewRequest существует всегда: note передаётся явным null. */
export const approveChangeRequest = (changeRequestId, note = null) =>
  withMockFallback(
    () => api
      .post(`/admin/change-requests/${changeRequestId}/approve`, { note })
      .then((response) => normalizeRequest(response.data)),
    () => approveChangeRequestMock(changeRequestId, { note }).then(normalizeRequest)
  );

export const rejectChangeRequest = (changeRequestId, payload = {}) =>
  withMockFallback(
    () => api
      .post(`/admin/change-requests/${changeRequestId}/reject`, {
        note: payload.note ?? payload.reason ?? null,
      })
      .then((response) => normalizeRequest(response.data)),
    () => rejectChangeRequestMock(changeRequestId, payload).then(normalizeRequest)
  );

/**
 * ТЗ 27: badge считается только из authoritative summary
 * GET /admin/change-requests/summary, а не из загруженной страницы списка.
 * Форма ответа в openapi не типизирована — читаем несколько известных ключей.
 */
export const normalizeChangeRequestSummary = (data = {}) => {
  const candidates = [
    data?.pending_count,
    data?.pending,
    data?.counts?.pending,
    data?.by_status?.pending,
    data?.total_pending,
  ];
  const value = candidates.find((item) => Number.isFinite(item));
  return { pendingCount: Number.isFinite(value) ? value : 0 };
};

export const getAdminChangeRequestSummary = () =>
  withMockFallback(
    () => api.get('/admin/change-requests/summary').then((response) => response.data),
    () => getChangeRequestSummaryMock()
  ).then(normalizeChangeRequestSummary);

/** ТЗ 30: список собственных заявок — GET /change-requests (production). */
export const listMyChangeRequests = () =>
  withMockFallback(
    () => api.get('/change-requests').then((response) => response.data),
    () => listMyChangeRequestsMock()
  ).then((data) => normalizeRequestList(data));
